import * as React from "react";
import { useLayoutEffect, useRef, useState } from "react";
import { ChatMessage, ComposerConfig, SuggestionCategory, WelcomeConfig } from "../../types";
import { Message, TypingMessage } from "../Message/Message";
import { Welcome } from "../Welcome/Welcome";
import { Input } from "../Input/Input";
import { Header } from "../Header/Header";
import { RespondingBackdrop } from "./components/RespondingBackdrop";
import styles from "./ChatSurface.module.css";

/** Distance from the bottom, in px, still treated as "at the bottom" for follow-scrolling. */
const PIN_THRESHOLD = 80;

interface Props {
	messages: ChatMessage[];
	isLoading: boolean;
	title: string;
	welcome?: WelcomeConfig;
	suggestions?: SuggestionCategory[];
	placeholder?: string;
	assistantIconUrl?: string;
	onSubmit: (text: string) => void;
	onAbort?: () => void;
	ended?: boolean;
	endedPlaceholder?: string;
	/** Small disclaimer under the composer; `null` hides it, `undefined` uses the default. */
	inputDisclaimer?: string | null;
	onSidebarToggle?: () => void;
	sidebarOpen?: boolean;
	onClose?: () => void;
	onFullScreenClick?: () => void;
	isFullScreen?: boolean;
	composer?: ComposerConfig;
	favourite?: boolean;
	onRenameSession?: (name: string) => void;
	onToggleFavourite?: () => void;
	onDeleteSession?: () => void;
	onFiles?: () => void;
	onShare?: () => void;
	/** Renders a compact light/dark toggle in the header when provided. */
	onThemeToggle?: () => void;
	/** Current theme, drives the header toggle icon. */
	theme?: "light" | "dark";
	/** Show the animated "rain" glow behind the composer while generating. Default true. */
	respondingAnimation?: boolean;
}

export const ChatSurface: React.FC<Props> = ({
	messages,
	isLoading,
	title,
	welcome,
	suggestions,
	placeholder,
	assistantIconUrl,
	onSubmit,
	onAbort,
	ended,
	endedPlaceholder,
	inputDisclaimer,
	onSidebarToggle,
	sidebarOpen,
	onClose,
	onFullScreenClick,
	isFullScreen,
	composer,
	favourite,
	onRenameSession,
	onToggleFavourite,
	onDeleteSession,
	onFiles,
	onShare,
	onThemeToggle,
	theme,
	respondingAnimation = true,
}) => {
	const scrollRef = useRef<HTMLDivElement>(null);
	const [scrolled, setScrolled] = useState(false);

	// Jump to the bottom whenever a message is added or the session changes. A streaming reply
	// grows in place — and ends by flipping isLoading — so both are followed only while the
	// reader is still at the bottom; scrolling up to read stops the follow.
	const pinnedRef = useRef(true);
	const lastCountRef = useRef(messages.length);
	const lastFirstIdRef = useRef(messages[0]?.id);

	// Layout effect: scroll before paint, or each streamed frame flashes below the fold first.
	useLayoutEffect(() => {
		const el = scrollRef.current;
		if (!el) return;
		const force = messages.length !== lastCountRef.current || messages[0]?.id !== lastFirstIdRef.current;
		lastCountRef.current = messages.length;
		lastFirstIdRef.current = messages[0]?.id;
		// Re-pin explicitly: a forced scroll that doesn't move scrollTop fires no scroll event.
		if (force) pinnedRef.current = true;
		if (pinnedRef.current) el.scrollTop = el.scrollHeight;
	}, [messages, isLoading]);

	const hasMessages = messages.length > 0;
	// A streaming reply shows its own progress, so the standalone typing row is for JSON replies.
	const showTyping = isLoading && messages.at(-1)?.status !== "streaming";

	return (
		<div className={styles.base}>
			<Header
				title={title}
				scrolled={scrolled}
				onSidebarToggle={onSidebarToggle}
				sidebarOpen={sidebarOpen}
				onClose={onClose}
				onFullScreenClick={onFullScreenClick}
				isFullScreen={isFullScreen}
				favourite={favourite}
				onRename={onRenameSession}
				onToggleFavourite={onToggleFavourite}
				onDelete={onDeleteSession}
				onFiles={onFiles}
				onShare={onShare}
				onThemeToggle={onThemeToggle}
				theme={theme}
			/>
			<div
				ref={scrollRef}
				className={styles.list}
				onScroll={(e) => {
					const el = e.currentTarget;
					setScrolled(el.scrollTop > 0);
					pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < PIN_THRESHOLD;
				}}
			>
				{!hasMessages ? (
					<Welcome welcome={welcome} suggestions={suggestions} onPick={onSubmit} />
				) : (
					<>
						{messages.map((m) => (
							<Message key={m.id} message={m} assistantIconUrl={assistantIconUrl} />
						))}
						{showTyping && <TypingMessage assistantIconUrl={assistantIconUrl} />}
					</>
				)}
			</div>
			<div className={styles.composerDock}>
				<div className={styles.scrim} aria-hidden />
				{isLoading && respondingAnimation && <RespondingBackdrop />}
				<Input
					loading={isLoading}
					placeholder={placeholder}
					disabled={ended}
					disabledPlaceholder={endedPlaceholder}
					onSubmit={onSubmit}
					onAbort={onAbort}
					composer={composer}
					disclaimer={inputDisclaimer}
				/>
			</div>
		</div>
	);
};
